import React, { useCallback, useEffect, useRef, useState } from 'react';
import { JaneAppTab } from './BloodTestIntegration';
import macPracticeLogo from '../../assets/integrations/mac_practice_logo.png';
import { useSelector } from 'react-redux';
import { RootState } from '../../store';
import styled, { keyframes } from 'styled-components';
import {
  startPatientUpload,
  setPiiSecret,
  loadPiiSecret,
  getPatients,
  getPatientAttachments,
  getAttachmentPresignedUrl,
  downloadPatientZip,
  getPatientEHRForms,
  getEHRFormContent,
  downloadPatientEHRZip,
  startJaneExport,
  getJaneExportStatus,
  getCrawlerJobStatus,
  PatientProfile,
  PatientAttachment,
  PatientEHRForm,
  CrawlerJob,
} from '../../services/api';
import { decryptPiiBatch, PatientPii } from '../../utils/piiCrypto';

const PII_SECRET_KEY = 'drschmoe_pii_secret';

function loadStoredSecret(): string { return localStorage.getItem(PII_SECRET_KEY) || ''; }
function saveSecret(hex: string): void { localStorage.setItem(PII_SECRET_KEY, hex); }

// ── Animations ────────────────────────────────────────────────────────────────

const pulse = keyframes`
  0%, 100% { opacity: 1; }
  50% { opacity: 0.45; }
`;

// ── Styled components ─────────────────────────────────────────────────────────

const Section = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const HeaderRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 12px;
`;

const SectionTitle = styled.h2`
  font-size: 1.0625rem;
  font-weight: 600;
  color: #111827;
  margin: 0;
`;

const UploadBtn = styled.button<{ $running?: boolean }>`
  padding: 9px 18px;
  font-size: 0.875rem;
  font-weight: 600;
  border: none;
  border-radius: 8px;
  cursor: ${p => (p.$running ? 'not-allowed' : 'pointer')};
  background: ${p =>
    p.$running
      ? 'linear-gradient(135deg, #94a3b8, #64748b)'
      : 'linear-gradient(135deg, #3b82f6, #2563eb)'};
  color: #fff;
  transition: filter 0.15s;
  &:hover:not(:disabled) { filter: brightness(1.07); }
`;

const ProgressBox = styled.div`
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  padding: 16px 20px;
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const ProgressLabel = styled.div`
  display: flex;
  justify-content: space-between;
  font-size: 0.8125rem;
  color: #475569;
`;

const ProgressTrack = styled.div`
  height: 8px;
  background: #e2e8f0;
  border-radius: 999px;
  overflow: hidden;
`;

const ProgressFill = styled.div<{ $pct: number; $done: boolean; $failed: boolean }>`
  height: 100%;
  width: ${p => p.$pct}%;
  border-radius: 999px;
  background: ${p =>
    p.$failed
      ? '#ef4444'
      : p.$done
      ? '#10b981'
      : 'linear-gradient(90deg, #3b82f6, #2563eb)'};
  transition: width 0.4s ease;
  animation: ${p => (!p.$done && !p.$failed ? pulse : 'none')} 2s ease-in-out infinite;
`;

const StatusMsg = styled.div`
  font-size: 0.8125rem;
  color: #64748b;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const PaginationRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 0.8125rem;
  color: #64748b;
`;

const PageBtn = styled.button<{ $active?: boolean }>`
  padding: 5px 10px;
  border-radius: 6px;
  border: 1px solid ${p => (p.$active ? '#3b82f6' : '#e2e8f0')};
  background: ${p => (p.$active ? '#eff6ff' : '#fff')};
  color: ${p => (p.$active ? '#1d4ed8' : '#374151')};
  font-size: 0.8125rem;
  cursor: pointer;
  &:disabled { opacity: 0.4; cursor: default; }
  &:hover:not(:disabled) { background: #f1f5f9; }
`;

const PatientTable = styled.div`
  border: 1px solid #e5e7eb;
  border-radius: 10px;
  overflow: hidden;
`;

const GRID_COLS = '28px minmax(80px,0.9fr) minmax(80px,0.9fr) minmax(70px,0.7fr) minmax(60px,0.5fr) minmax(70px,0.55fr) minmax(70px,0.65fr) 120px 120px';

const JaneExportBar = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 0 2px;
  flex-wrap: wrap;
`;

const JaneBtn = styled.button<{ $variant?: 'primary' | 'secondary' }>`
  padding: 6px 14px;
  font-size: 0.8rem;
  font-weight: 600;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  background: ${p => p.$variant === 'secondary' ? '#f1f5f9' : 'linear-gradient(135deg, #10b981, #059669)'};
  color: ${p => p.$variant === 'secondary' ? '#374151' : '#fff'};
  border: 1px solid ${p => p.$variant === 'secondary' ? '#e2e8f0' : 'transparent'};
  transition: filter 0.15s;
  &:hover:not(:disabled) { filter: brightness(1.07); }
  &:disabled { opacity: 0.45; cursor: not-allowed; }
`;

const TableHeader = styled.div`
  display: grid;
  grid-template-columns: ${GRID_COLS};
  padding: 10px 16px;
  background: #f8fafc;
  border-bottom: 1px solid #e5e7eb;
  font-size: 0.75rem;
  font-weight: 600;
  color: #64748b;
  text-transform: uppercase;
  letter-spacing: 0.04em;
`;

const SortableHeaderCell = styled.span<{ $active: boolean }>`
  cursor: pointer;
  user-select: none;
  display: flex;
  align-items: center;
  gap: 4px;
  color: ${p => (p.$active ? '#1d4ed8' : '#64748b')};
  &:hover { color: #1d4ed8; }
`;

const PatientRow = styled.div<{ $expanded: boolean }>`
  border-bottom: 1px solid #f1f5f9;
  background: ${p => (p.$expanded ? '#f0f9ff' : '#fff')};
  &:last-child { border-bottom: none; }
`;

const PatientRowMain = styled.div`
  display: grid;
  grid-template-columns: ${GRID_COLS};
  padding: 11px 16px;
  align-items: center;
  cursor: pointer;
  &:hover { background: #f8fafc; }
`;

const Cell = styled.div`
  font-size: 0.8125rem;
  color: #374151;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const UuidCell = styled(Cell)`
  font-family: 'SF Mono', 'Fira Code', monospace;
  font-size: 0.7rem;
  color: #94a3b8;
`;

const BadgeCount = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: #dbeafe;
  color: #1d4ed8;
  font-size: 0.7rem;
  font-weight: 600;
  border-radius: 999px;
  padding: 2px 8px;
`;

const ChevronBtn = styled.button<{ $open: boolean }>`
  background: none;
  border: none;
  cursor: pointer;
  font-size: 1rem;
  color: #64748b;
  transform: rotate(${p => (p.$open ? '90deg' : '0deg')});
  transition: transform 0.15s;
  padding: 0 4px;
`;

const AttachmentPanel = styled.div`
  padding: 8px 16px 14px 32px;
  display: flex;
  flex-direction: column;
  gap: 6px;
  background: #f8fafc;
  border-top: 1px solid #e0f2fe;
`;

const AttachmentRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 7px 10px;
  background: #fff;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
`;

const AttachmentName = styled.span`
  font-size: 0.8125rem;
  color: #1e293b;
  flex: 1;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const AttachmentMeta = styled.span`
  font-size: 0.75rem;
  color: #94a3b8;
  white-space: nowrap;
`;

const ViewBtn = styled.button`
  padding: 5px 12px;
  font-size: 0.75rem;
  font-weight: 600;
  border: 1px solid #3b82f6;
  border-radius: 6px;
  background: #eff6ff;
  color: #2563eb;
  cursor: pointer;
  white-space: nowrap;
  &:hover { background: #dbeafe; }
`;

const DownloadBtn = styled.button`
  padding: 5px 12px;
  font-size: 0.75rem;
  font-weight: 600;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  background: #f9fafb;
  color: #374151;
  cursor: pointer;
  white-space: nowrap;
  &:hover { background: #f1f5f9; }
`;

const PdfBackdrop = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(15, 23, 42, 0.6);
  z-index: 2000;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
`;

const PdfFrame = styled.div`
  width: min(960px, 96vw);
  height: min(88vh, 900px);
  background: #0f172a;
  border-radius: 12px;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  box-shadow: 0 25px 50px rgba(0,0,0,0.4);
`;

const PdfIframe = styled.iframe`
  flex: 1;
  width: 100%;
  border: none;
  background: #fff;
`;

const PdfClose = styled.button`
  flex-shrink: 0;
  padding: 14px 24px;
  font-size: 0.9375rem;
  font-weight: 600;
  color: #e2e8f0;
  background: #1e293b;
  border: none;
  cursor: pointer;
  &:hover { background: #334155; }
`;

const EHRPanel = styled.div`
  padding: 8px 16px 14px 32px;
  display: flex;
  flex-direction: column;
  gap: 6px;
  background: #f0fdf4;
  border-top: 1px solid #bbf7d0;
`;

const EHRRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 7px 10px;
  background: #fff;
  border: 1px solid #d1fae5;
  border-radius: 8px;
`;

const EHRName = styled.span`
  font-size: 0.8125rem;
  color: #1e293b;
  flex: 1;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const EHRBadgeCount = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: #d1fae5;
  color: #065f46;
  font-size: 0.7rem;
  font-weight: 600;
  border-radius: 999px;
  padding: 2px 8px;
`;

const HtmlBackdrop = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(15, 23, 42, 0.6);
  z-index: 2000;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
`;

const HtmlFrame = styled.div`
  width: min(960px, 96vw);
  height: min(88vh, 900px);
  background: #0f172a;
  border-radius: 12px;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  box-shadow: 0 25px 50px rgba(0,0,0,0.4);
`;

const HtmlIframe = styled.iframe`
  flex: 1;
  width: 100%;
  border: none;
  background: #fff;
`;

const HtmlClose = styled.button`
  flex-shrink: 0;
  padding: 14px 24px;
  font-size: 0.9375rem;
  font-weight: 600;
  color: #e2e8f0;
  background: #1e293b;
  border: none;
  cursor: pointer;
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
  &:hover { background: #334155; }
`;

const PrintBtn = styled.button`
  padding: 6px 14px;
  font-size: 0.8125rem;
  font-weight: 600;
  border: 1px solid #475569;
  border-radius: 6px;
  background: #334155;
  color: #e2e8f0;
  cursor: pointer;
  &:hover { background: #475569; }
`;

const EmptyState = styled.div`
  padding: 48px 24px;
  text-align: center;
  color: #64748b;
  font-size: 0.9375rem;
  background: #f9fafb;
  border-radius: 12px;
  border: 1px dashed #e5e7eb;
`;

const SecretBanner = styled.div<{ $unlocked?: boolean }>`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 16px;
  background: ${p => (p.$unlocked ? '#f0fdf4' : '#fefce8')};
  border: 1px solid ${p => (p.$unlocked ? '#86efac' : '#fde68a')};
  border-radius: 10px;
  font-size: 0.8125rem;
  color: ${p => (p.$unlocked ? '#166534' : '#92400e')};
  flex-wrap: wrap;
`;

const SecretLoadBtn = styled.button<{ $loading?: boolean }>`
  padding: 7px 14px;
  font-size: 0.8125rem;
  font-weight: 600;
  border: none;
  border-radius: 6px;
  background: ${p => (p.$loading ? '#9ca3af' : '#d97706')};
  color: #fff;
  cursor: ${p => (p.$loading ? 'not-allowed' : 'pointer')};
  white-space: nowrap;
  &:hover:not(:disabled) { background: #b45309; }
`;

const SecretClearBtn = styled.button`
  padding: 6px 10px;
  font-size: 0.75rem;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  background: none;
  color: #6b7280;
  cursor: pointer;
  white-space: nowrap;
  &:hover { background: #f3f4f6; }
`;

// ── Sub-tab bar (exported for use in BloodTestIntegration) ────────────────────

export const EhrSubTabBar = styled.div`
  display: flex;
  gap: 0;
  border-bottom: 2px solid #e5e7eb;
  margin-bottom: 16px;
`;

export const EhrSubTabBtn = styled.button<{ $active: boolean }>`
  padding: 10px 24px;
  font-size: 0.875rem;
  font-weight: 600;
  border: none;
  background: none;
  cursor: pointer;
  color: ${p => (p.$active ? '#2563eb' : '#6b7280')};
  border-bottom: 2px solid ${p => (p.$active ? '#2563eb' : 'transparent')};
  margin-bottom: -2px;
  transition: color 0.15s, border-color 0.15s;
  &:hover { color: #2563eb; }
`;

// ── Helpers ───────────────────────────────────────────────────────────────────

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
}

function shortUuid(uuid: string): string {
  return uuid.slice(0, 8) + '…';
}

// ── Component ─────────────────────────────────────────────────────────────────

const POLL_MS = 3000;
const PAGE_SIZE = 50;

const BATCH_OPTIONS = [
  { label: '10 patients (test)', value: 10 },
  { label: '50 patients', value: 50 },
  { label: '100 patients', value: 100 },
  { label: 'All patients', value: 0 },
] as const;

// ── PatientListPanel — vendor-agnostic patient list (exported) ────────────────

export const PatientListPanel: React.FC<{ sourceVendor: string }> = ({ sourceVendor }) => {
  const isMac = sourceVendor === 'macpractice';

  const [patients, setPatients] = useState<PatientProfile[]>([]);
  const [totalPatients, setTotalPatients] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [page, setPage] = useState(1);
  const [loadingPatients, setLoadingPatients] = useState(false);
  const [uuidSearch, setUuidSearch] = useState('');

  const [expandedAtt, setExpandedAtt] = useState<Record<string, boolean>>({});
  const [expandedEhr, setExpandedEhr] = useState<Record<string, boolean>>({});
  const [attachments, setAttachments] = useState<Record<string, PatientAttachment[]>>({});
  const [ehrForms, setEhrForms] = useState<Record<string, PatientEHRForm[]>>({});
  const [loadingAtt, setLoadingAtt] = useState<Record<string, boolean>>({});
  const [loadingEhr, setLoadingEhr] = useState<Record<string, boolean>>({});

  const [pdfUrl, setPdfUrl] = useState<string | null>(null);
  const [pdfName, setPdfName] = useState<string>('');
  const [ehrHtml, setEhrHtml] = useState<string | null>(null);
  const [ehrName, setEhrName] = useState<string>('');

  const [secretHex, setSecretHex] = useState<string>(loadStoredSecret);
  const [secretLoading, setSecretLoading] = useState(false);
  const [secretError, setSecretError] = useState<string | null>(null);
  const [piiMap, setPiiMap] = useState<Record<string, PatientPii>>({});
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  type SortCol = 'first_name' | 'last_name' | 'birthday' | 'new_patient_date';
  const [sortCol, setSortCol] = useState<SortCol>('last_name');
  const [sortAsc, setSortAsc] = useState(true);

  const [selectedUuids, setSelectedUuids] = useState<Set<string>>(new Set());
  const [janeExporting, setJaneExporting] = useState(false);
  const [janeError, setJaneError] = useState<string | null>(null);
  const [janeJobId, setJaneJobId] = useState<string | null>(null);
  const [janeProcessed, setJaneProcessed] = useState(0);
  const [janeTotal, setJaneTotal] = useState(0);
  const janePollRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const handleSort = (col: SortCol) => {
    if (sortCol === col) setSortAsc(a => !a);
    else { setSortCol(col); setSortAsc(true); }
  };

  const toggleSelect = (uuid: string) => {
    setSelectedUuids(prev => {
      const next = new Set(prev);
      if (next.has(uuid)) next.delete(uuid); else next.add(uuid);
      return next;
    });
  };

  const toggleSelectAll = () => {
    if (selectedUuids.size === patients.length) setSelectedUuids(new Set());
    else setSelectedUuids(new Set(patients.map(p => p.patient_uuid)));
  };

  const stopJanePoll = useCallback(() => {
    if (janePollRef.current) { clearInterval(janePollRef.current); janePollRef.current = null; }
  }, []);

  const handleJaneExport = async (uuids: string[], exportAll = false) => {
    if (!exportAll && uuids.length === 0) return;
    if (!secretHex) { setJaneError('PII key not loaded. Load the secret key first.'); return; }
    setJaneExporting(true); setJaneError(null); setJaneJobId(null);
    setJaneProcessed(0); setJaneTotal(0); stopJanePoll();
    try {
      const { job_id } = await startJaneExport(uuids, secretHex, exportAll);
      setJaneJobId(job_id);
      janePollRef.current = setInterval(async () => {
        try {
          const s = await getJaneExportStatus(job_id);
          setJaneProcessed(s.processed ?? 0);
          setJaneTotal(s.total ?? 0);
          if (s.status === 'done') {
            stopJanePoll(); setJaneExporting(false); setJaneJobId(null);
            if (s.url) {
              const a = document.createElement('a');
              a.href = s.url;
              a.download = `jane_export_${new Date().toISOString().slice(0, 10)}.zip`;
              a.click();
            }
          } else if (s.status === 'failed') {
            stopJanePoll(); setJaneExporting(false); setJaneJobId(null);
            setJaneError(s.error || 'Jane export failed');
          }
        } catch { /* transient */ }
      }, 5000);
    } catch (e: any) {
      setJaneExporting(false);
      setJaneError(e?.message || 'Jane export failed to start');
    }
  };

  const decryptAndMerge = useCallback(async (rows: PatientProfile[], hex: string) => {
    if (!hex || hex.length !== 64) return;
    try {
      const newPii = await decryptPiiBatch(hex, rows.map(r => ({ patient_uuid: r.patient_uuid, encrypted_pii: r.encrypted_pii })));
      setPiiMap(prev => ({ ...prev, ...newPii }));
    } catch { /* bad key */ }
  }, []);

  const loadPatients = useCallback(async (p: number, hex?: string, search?: string) => {
    setLoadingPatients(true);
    try {
      const q = search !== undefined ? search : uuidSearch;
      const res = await getPatients(p, PAGE_SIZE, sourceVendor, q || undefined);
      setPatients(res.patients);
      setTotalPatients(res.total);
      setTotalPages(res.total_pages);
      setPage(p);
      await decryptAndMerge(res.patients, hex ?? secretHex);
    } catch { /* ignore */ } finally {
      setLoadingPatients(false);
    }
  }, [secretHex, decryptAndMerge, sourceVendor, uuidSearch]);

  const handleLoadSecret = async () => {
    setSecretLoading(true); setSecretError(null);
    try {
      const res = await loadPiiSecret();
      if (res.error) { setSecretError(res.error); return; }
      const hex = res.secret_hex!;
      saveSecret(hex); setSecretHex(hex);
      await decryptAndMerge(patients, hex);
    } catch (e: any) {
      setSecretError(e?.data?.error || e?.message || 'Failed to load secret from agent.');
    } finally { setSecretLoading(false); }
  };

  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    e.target.value = '';
    setSecretLoading(true); setSecretError(null);
    try {
      const buf = await file.arrayBuffer();
      const bytes = new Uint8Array(buf);
      if (bytes.length !== 32) {
        setSecretError(`Invalid file — expected 32 bytes, got ${bytes.length}. Make sure you selected the correct secret.key file.`);
        return;
      }
      const hex = Array.from(bytes).map(b => b.toString(16).padStart(2, '0')).join('');
      const res = await setPiiSecret(hex);
      if (res.error) { setSecretError(res.error); return; }
      saveSecret(hex); setSecretHex(hex);
      await decryptAndMerge(patients, hex);
    } catch (e: any) {
      setSecretError(e?.data?.error || e?.message || 'Failed to load secret file.');
    } finally { setSecretLoading(false); }
  };

  const handleClearSecret = () => {
    localStorage.removeItem(PII_SECRET_KEY);
    setSecretHex(''); setSecretError(null); setPiiMap({});
  };

  useEffect(() => { loadPatients(1); }, [loadPatients]);
  useEffect(() => () => { stopJanePoll(); }, [stopJanePoll]);

  const toggleExpandAtt = async (uuid: string) => {
    const next = !expandedAtt[uuid];
    setExpandedAtt(prev => ({ ...prev, [uuid]: next }));
    if (next && !attachments[uuid]) {
      setLoadingAtt(prev => ({ ...prev, [uuid]: true }));
      try {
        const res = await getPatientAttachments(uuid, sourceVendor);
        setAttachments(prev => ({ ...prev, [uuid]: res.attachments }));
      } catch {
        setAttachments(prev => ({ ...prev, [uuid]: [] }));
      } finally { setLoadingAtt(prev => ({ ...prev, [uuid]: false })); }
    }
  };

  const toggleExpandEhr = async (uuid: string) => {
    const next = !expandedEhr[uuid];
    setExpandedEhr(prev => ({ ...prev, [uuid]: next }));
    if (next && !ehrForms[uuid]) {
      setLoadingEhr(prev => ({ ...prev, [uuid]: true }));
      try {
        const res = await getPatientEHRForms(uuid);
        setEhrForms(prev => ({ ...prev, [uuid]: res.ehr_forms }));
      } catch {
        setEhrForms(prev => ({ ...prev, [uuid]: [] }));
      } finally { setLoadingEhr(prev => ({ ...prev, [uuid]: false })); }
    }
  };

  const openPdf = async (attId: string) => {
    try {
      const res = await getAttachmentPresignedUrl(attId, 'view');
      setPdfName(res.file_name); setPdfUrl(res.url);
    } catch { alert('Could not load PDF. Please try again.'); }
  };

  const downloadPdf = async (attId: string) => {
    try {
      const res = await getAttachmentPresignedUrl(attId, 'download');
      const a = document.createElement('a');
      a.href = res.url; a.download = res.file_name; a.click();
    } catch { alert('Could not download PDF. Please try again.'); }
  };

  const downloadAllZip = async (patientUuid: string) => {
    try { await downloadPatientZip(patientUuid, sourceVendor); }
    catch { alert('Could not download ZIP. Please try again.'); }
  };

  const openEhrForm = async (formId: string) => {
    try {
      const res = await getEHRFormContent(formId);
      setEhrName(res.file_name); setEhrHtml(res.html_content);
    } catch { alert('Could not load EHR form. Please try again.'); }
  };

  const printEhrForm = (html: string) => {
    const w = window.open('', '_blank');
    if (!w) return;
    w.document.write(html); w.document.close(); w.focus(); w.print();
  };

  return (
    <Section>
      {secretHex ? (
        <SecretBanner $unlocked>
          <span>Patient names unlocked</span>
          <SecretClearBtn onClick={handleClearSecret}>Clear key</SecretClearBtn>
        </SecretBanner>
      ) : (
        <SecretBanner>
          <input ref={fileInputRef} type="file" accept=".key,application/octet-stream"
            style={{ display: 'none' }} onChange={handleFileSelect} />
          <span style={{ fontWeight: 600 }}>Patient names are encrypted.</span>
          <span style={{ flex: 1 }}>
            Select the <code style={{ background: '#fef3c7', padding: '1px 5px', borderRadius: 3 }}>secret.key</code> file
            from your group admin to display patient names.
          </span>
          <SecretLoadBtn $loading={secretLoading} onClick={() => fileInputRef.current?.click()} disabled={secretLoading}>
            {secretLoading ? 'Loading…' : 'Select secret.key…'}
          </SecretLoadBtn>
          <SecretClearBtn onClick={handleLoadSecret} disabled={secretLoading}
            style={{ opacity: secretLoading ? 0.5 : 1 }} title="If your agent app already has the key installed">
            Load from Agent
          </SecretClearBtn>
          {secretError && <span style={{ width: '100%', color: '#b45309', marginTop: 4 }}>{secretError}</span>}
        </SecretBanner>
      )}

      <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
        <input
          type="text"
          placeholder="Search by UUID prefix…"
          value={uuidSearch}
          onChange={e => setUuidSearch(e.target.value)}
          onKeyDown={e => { if (e.key === 'Enter') loadPatients(1, undefined, uuidSearch); }}
          style={{
            flex: 1, padding: '8px 12px', fontSize: '0.875rem', borderRadius: 8,
            border: '1px solid #d1d5db', outline: 'none',
          }}
        />
        <UploadBtn $running={false} onClick={() => loadPatients(1, undefined, uuidSearch)}
          style={{ background: 'linear-gradient(135deg, #6366f1, #4f46e5)', minWidth: 80 }}>
          Search
        </UploadBtn>
        {uuidSearch && (
          <UploadBtn $running={false} onClick={() => { setUuidSearch(''); loadPatients(1, undefined, ''); }}
            style={{ background: '#6b7280', minWidth: 70 }}>
            Clear
          </UploadBtn>
        )}
      </div>

      {loadingPatients && patients.length === 0 ? (
        <EmptyState>Loading patients…</EmptyState>
      ) : patients.length === 0 ? (
        <EmptyState>No patients found for this vendor.</EmptyState>
      ) : (
        <>
          {isMac && (
            <JaneExportBar>
              <span style={{ fontSize: '0.8rem', color: '#6b7280', fontWeight: 600 }}>Jane App Export:</span>
              <JaneBtn disabled={selectedUuids.size === 0 || janeExporting}
                onClick={() => handleJaneExport([...selectedUuids])}>
                {janeExporting && !janeJobId ? 'Starting…' : `Export selected (${selectedUuids.size})`}
              </JaneBtn>
              <JaneBtn $variant="secondary" disabled={janeExporting || totalPatients === 0}
                onClick={() => handleJaneExport([], true)}>
                Export all ({totalPatients.toLocaleString()})
              </JaneBtn>
              {janeExporting && janeTotal > 0 && (
                <span style={{ fontSize: '0.8rem', color: '#059669', fontWeight: 500 }}>
                  Building ZIP… {janeProcessed} / {janeTotal} patients
                </span>
              )}
              {janeExporting && janeTotal === 0 && (
                <span style={{ fontSize: '0.8rem', color: '#059669' }}>Starting export…</span>
              )}
              {janeError && <span style={{ fontSize: '0.8rem', color: '#ef4444' }}>{janeError}</span>}
            </JaneExportBar>
          )}

          <PatientTable>
            <TableHeader>
              {isMac && (
                <input type="checkbox" style={{ cursor: 'pointer', accentColor: '#10b981' }}
                  checked={patients.length > 0 && selectedUuids.size === patients.length}
                  onChange={toggleSelectAll} title="Select all" />
              )}
              {(['first_name', 'last_name'] as const).map(col => (
                <SortableHeaderCell key={col} $active={sortCol === col} onClick={() => handleSort(col)}>
                  {col === 'first_name' ? 'First Name' : 'Last Name'}
                  {sortCol === col ? (sortAsc ? ' ↑' : ' ↓') : ' ↕'}
                </SortableHeaderCell>
              ))}
              <SortableHeaderCell $active={sortCol === 'birthday'} onClick={() => handleSort('birthday')}>
                Birthday{sortCol === 'birthday' ? (sortAsc ? ' ↑' : ' ↓') : ' ↕'}
              </SortableHeaderCell>
              <span>Sex / Age</span>
              <span>Source</span>
              <SortableHeaderCell $active={sortCol === 'new_patient_date'} onClick={() => handleSort('new_patient_date')}>
                First Visit{sortCol === 'new_patient_date' ? (sortAsc ? ' ↑' : ' ↓') : ' ↕'}
              </SortableHeaderCell>
              <span>Attachments</span>
              <span>EHR</span>
            </TableHeader>

            {[...patients].sort((a, b) => {
              let av = '', bv = '';
              if (sortCol === 'first_name') { av = piiMap[a.patient_uuid]?.first_name ?? ''; bv = piiMap[b.patient_uuid]?.first_name ?? ''; }
              else if (sortCol === 'last_name') { av = piiMap[a.patient_uuid]?.last_name ?? ''; bv = piiMap[b.patient_uuid]?.last_name ?? ''; }
              else if (sortCol === 'birthday') { av = piiMap[a.patient_uuid]?.birthday ?? ''; bv = piiMap[b.patient_uuid]?.birthday ?? ''; }
              else { av = a.new_patient_date ?? ''; bv = b.new_patient_date ?? ''; }
              const cmp = av.localeCompare(bv);
              return sortAsc ? cmp : -cmp;
            }).map(p => (
              <PatientRow key={p.patient_uuid} $expanded={!!(expandedAtt[p.patient_uuid] || expandedEhr[p.patient_uuid])}>
                <PatientRowMain onClick={e => e.stopPropagation()}>
                  {isMac && (
                    <div style={{ display: 'flex', alignItems: 'center' }}>
                      <input type="checkbox" style={{ cursor: 'pointer', accentColor: '#10b981' }}
                        checked={selectedUuids.has(p.patient_uuid)}
                        onChange={() => toggleSelect(p.patient_uuid)} />
                    </div>
                  )}
                  <Cell title={p.patient_uuid}>
                    {piiMap[p.patient_uuid]?.first_name
                      ? <span style={{ fontWeight: 500 }}>{piiMap[p.patient_uuid].first_name}</span>
                      : <UuidCell as="span">{shortUuid(p.patient_uuid)}</UuidCell>}
                    {p.display_id && (
                      <span style={{ color: '#9ca3af', marginLeft: 6, fontSize: '0.75rem' }}>#{p.display_id}</span>
                    )}
                  </Cell>
                  <Cell>
                    {piiMap[p.patient_uuid]?.last_name
                      ? <span style={{ fontWeight: 500 }}>{piiMap[p.patient_uuid].last_name}</span>
                      : '—'}
                  </Cell>
                  <Cell>{piiMap[p.patient_uuid]?.birthday ?? '—'}</Cell>
                  <Cell>{p.sex ?? '—'} / {p.age != null ? `${p.age}y` : '—'}</Cell>
                  <Cell>{p.source_vendor}</Cell>
                  <Cell>{p.new_patient_date ?? '—'}</Cell>
                  <Cell style={{ cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 4 }}
                    onClick={() => toggleExpandAtt(p.patient_uuid)}>
                    <BadgeCount>{p.attachment_count}</BadgeCount>
                    <ChevronBtn $open={!!expandedAtt[p.patient_uuid]}>›</ChevronBtn>
                  </Cell>
                  <Cell style={{ cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 4 }}
                    onClick={() => toggleExpandEhr(p.patient_uuid)}>
                    <EHRBadgeCount>{p.ehr_form_count ?? 0}</EHRBadgeCount>
                    <ChevronBtn $open={!!expandedEhr[p.patient_uuid]}>›</ChevronBtn>
                  </Cell>
                </PatientRowMain>

                {expandedAtt[p.patient_uuid] && (
                  <AttachmentPanel>
                    {loadingAtt[p.patient_uuid] ? (
                      <StatusMsg>Loading attachments…</StatusMsg>
                    ) : (attachments[p.patient_uuid] || []).length === 0 ? (
                      <StatusMsg>No attachments.</StatusMsg>
                    ) : (
                      <>
                        <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 4 }}>
                          <DownloadBtn onClick={() => downloadAllZip(p.patient_uuid)}>
                            ↓ Download All (.zip)
                          </DownloadBtn>
                        </div>
                        {(attachments[p.patient_uuid] || []).map(att => (
                          <AttachmentRow key={att.id}>
                            <AttachmentName title={att.file_name}>{att.file_name}</AttachmentName>
                            <AttachmentMeta>{formatBytes(att.file_size)}</AttachmentMeta>
                            {att.attached_date && <AttachmentMeta>{att.attached_date}</AttachmentMeta>}
                            <ViewBtn onClick={() => openPdf(att.id)}>View</ViewBtn>
                            <DownloadBtn onClick={() => downloadPdf(att.id)}>↓</DownloadBtn>
                          </AttachmentRow>
                        ))}
                      </>
                    )}
                  </AttachmentPanel>
                )}

                {expandedEhr[p.patient_uuid] && (
                  <EHRPanel>
                    {loadingEhr[p.patient_uuid] ? (
                      <StatusMsg>Loading EHR forms…</StatusMsg>
                    ) : (ehrForms[p.patient_uuid] || []).length === 0 ? (
                      <StatusMsg>No EHR forms.</StatusMsg>
                    ) : (
                      <>
                        <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 4 }}>
                          <DownloadBtn onClick={() => downloadPatientEHRZip(p.patient_uuid).catch((e: any) => alert(e?.data?.error || e?.message || 'Could not download ZIP.'))}>
                            ↓ Download All (.zip)
                          </DownloadBtn>
                        </div>
                        {(ehrForms[p.patient_uuid] || []).map(form => (
                          <EHRRow key={form.id}>
                            <EHRName title={form.template_name ?? form.file_name}>
                              {form.template_name ?? form.file_name}
                            </EHRName>
                            {form.form_date && <AttachmentMeta>{form.form_date}</AttachmentMeta>}
                            <ViewBtn onClick={() => openEhrForm(form.id)}>View</ViewBtn>
                            <DownloadBtn onClick={async () => {
                              try {
                                const res = await getEHRFormContent(form.id);
                                printEhrForm(res.html_content);
                              } catch { alert('Could not load form for printing.'); }
                            }}>↓ PDF</DownloadBtn>
                          </EHRRow>
                        ))}
                      </>
                    )}
                  </EHRPanel>
                )}
              </PatientRow>
            ))}
          </PatientTable>

          {totalPages > 1 && (
            <PaginationRow>
              {(() => {
                const WIN = 7;
                const groupStart = Math.floor((page - 1) / WIN) * WIN + 1;
                const groupEnd = Math.min(groupStart + WIN - 1, totalPages);
                return (
                  <>
                    <PageBtn disabled={groupStart <= 1} onClick={() => loadPatients(groupStart - 1)}>←</PageBtn>
                    {Array.from({ length: groupEnd - groupStart + 1 }, (_, i) => {
                      const pg = groupStart + i;
                      return <PageBtn key={pg} $active={pg === page} onClick={() => loadPatients(pg)}>{pg}</PageBtn>;
                    })}
                    <PageBtn disabled={groupEnd >= totalPages} onClick={() => loadPatients(groupStart + WIN)}>→</PageBtn>
                    <span style={{ marginLeft: 8 }}>{totalPatients.toLocaleString()} total</span>
                  </>
                );
              })()}
            </PaginationRow>
          )}
        </>
      )}

      {pdfUrl && (
        <PdfBackdrop onClick={() => { setPdfUrl(null); setPdfName(''); }}>
          <PdfFrame onClick={e => e.stopPropagation()}>
            <PdfClose onClick={() => { setPdfUrl(null); setPdfName(''); }}>✕ Close — {pdfName}</PdfClose>
            <PdfIframe src={pdfUrl} title={pdfName} />
          </PdfFrame>
        </PdfBackdrop>
      )}

      {ehrHtml && (
        <HtmlBackdrop onClick={() => { setEhrHtml(null); setEhrName(''); }}>
          <HtmlFrame onClick={e => e.stopPropagation()}>
            <HtmlClose onClick={() => { setEhrHtml(null); setEhrName(''); }}>
              <span>✕ Close — {ehrName}</span>
              <PrintBtn onClick={() => printEhrForm(ehrHtml)}>↓ Download PDF</PrintBtn>
            </HtmlClose>
            <HtmlIframe title={ehrName} srcDoc={ehrHtml} sandbox="allow-same-origin" />
          </HtmlFrame>
        </HtmlBackdrop>
      )}
    </Section>
  );
};

// ── MacPracticeTab ─────────────────────────────────────────────────────────────

const MacPracticeTab: React.FC = () => {
  const groupName = useSelector((s: RootState) => s.user.profile?.group_name);
  const [activeTab, setActiveTab] = useState<'automation' | 'patients'>('automation');
  const [uploadJob, setUploadJob] = useState<CrawlerJob | null>(null);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [ehrUploadJob, setEhrUploadJob] = useState<CrawlerJob | null>(null);
  const [ehrUploading, setEhrUploading] = useState(false);
  const [ehrUploadError, setEhrUploadError] = useState<string | null>(null);
  const [batchLimit, setBatchLimit] = useState<number>(10);
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const ehrPollRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const stopPoll = useCallback(() => {
    if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
  }, []);

  const stopEhrPoll = useCallback(() => {
    if (ehrPollRef.current) { clearInterval(ehrPollRef.current); ehrPollRef.current = null; }
  }, []);

  const pollJob = useCallback(async (jobId: string) => {
    try {
      const job = await getCrawlerJobStatus(jobId);
      setUploadJob(job);
      if (job.status === 'completed' || job.status === 'failed') {
        stopPoll(); setUploading(false);
      }
    } catch { stopPoll(); setUploading(false); }
  }, [stopPoll]);

  const pollEhrJob = useCallback(async (jobId: string) => {
    try {
      const job = await getCrawlerJobStatus(jobId);
      setEhrUploadJob(job);
      if (job.status === 'completed' || job.status === 'failed') {
        stopEhrPoll(); setEhrUploading(false);
      }
    } catch { stopEhrPoll(); setEhrUploading(false); }
  }, [stopEhrPoll]);

  const handleUpload = async () => {
    setUploadError(null); setUploading(true);
    try {
      const res = await startPatientUpload('macpractice', batchLimit > 0 ? batchLimit : undefined);
      if (!res.success || !res.job_id) {
        setUploadError(res.error || 'Failed to start upload'); setUploading(false); return;
      }
      pollRef.current = setInterval(() => pollJob(res.job_id!), POLL_MS);
    } catch (e: any) {
      setUploadError(e?.data?.error || e?.message || 'Failed to start upload'); setUploading(false);
    }
  };

  useEffect(() => () => { stopPoll(); stopEhrPoll(); }, [stopPoll, stopEhrPoll]);

  const uploadRunning = uploading || uploadJob?.status === 'running';
  const uploadDone = uploadJob?.status === 'completed';
  const uploadFailed = uploadJob?.status === 'failed';

  const ehrRunning = ehrUploading || ehrUploadJob?.status === 'running';
  const ehrDone = ehrUploadJob?.status === 'completed';
  const ehrFailed = ehrUploadJob?.status === 'failed';

  return (
    <Section>
      <EhrSubTabBar>
        <EhrSubTabBtn $active={activeTab === 'automation'} onClick={() => setActiveTab('automation')}>
          Automation
        </EhrSubTabBtn>
        <EhrSubTabBtn $active={activeTab === 'patients'} onClick={() => setActiveTab('patients')}>
          Patients
        </EhrSubTabBtn>
      </EhrSubTabBar>

      {activeTab === 'automation' && (
        <>
          <HeaderRow>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              <img src={macPracticeLogo} alt="MacPractice" style={{ height: 28, objectFit: 'contain' }} />
              <SectionTitle>Patient Intake — {groupName ?? 'My Clinic'}</SectionTitle>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <select
                value={batchLimit}
                onChange={e => setBatchLimit(Number(e.target.value))}
                disabled={uploadRunning}
                style={{
                  padding: '8px 12px', fontSize: '0.875rem', borderRadius: 8,
                  border: '1px solid #d1d5db', background: '#fff', color: '#374151',
                  cursor: uploadRunning ? 'not-allowed' : 'pointer',
                }}
              >
                {BATCH_OPTIONS.map(o => (
                  <option key={o.value} value={o.value}>{o.label}</option>
                ))}
              </select>
              <UploadBtn $running={uploadRunning} onClick={handleUpload} disabled={uploadRunning}>
                {uploadRunning ? 'Uploading…' : 'Upload Attachments'}
              </UploadBtn>
              <UploadBtn
                $running={ehrRunning}
                onClick={() => {
                  setEhrUploadError(null); setEhrUploading(true);
                  startPatientUpload('macpractice_ehr', batchLimit > 0 ? batchLimit : undefined)
                    .then(res => {
                      if (!res.success || !res.job_id) {
                        setEhrUploadError(res.error || 'Failed to start EHR upload');
                        setEhrUploading(false); return;
                      }
                      ehrPollRef.current = setInterval(() => pollEhrJob(res.job_id!), POLL_MS);
                    })
                    .catch((e: any) => {
                      setEhrUploadError(e?.data?.error || e?.message || 'Failed to start EHR upload');
                      setEhrUploading(false);
                    });
                }}
                disabled={ehrRunning}
                style={{ background: ehrRunning ? undefined : 'linear-gradient(135deg, #10b981, #059669)' }}
              >
                {ehrRunning ? 'Uploading…' : 'Upload EHR Forms'}
              </UploadBtn>
            </div>
          </HeaderRow>

          {uploadError && <StatusMsg style={{ color: '#ef4444' }}>{uploadError}</StatusMsg>}

          {uploadJob && (
            <ProgressBox>
              <ProgressLabel>
                <span>{uploadJob.message || `${uploadJob.current_page} / ${uploadJob.total_pages} files`}</span>
                <span>{uploadJob.percent.toFixed(1)}%</span>
              </ProgressLabel>
              <ProgressTrack>
                <ProgressFill $pct={uploadJob.percent} $done={uploadDone} $failed={uploadFailed} />
              </ProgressTrack>
              {uploadFailed && <StatusMsg style={{ color: '#ef4444' }}>{uploadJob.error}</StatusMsg>}
            </ProgressBox>
          )}

          {ehrUploadError && <StatusMsg style={{ color: '#ef4444' }}>{ehrUploadError}</StatusMsg>}

          {ehrUploadJob && (
            <ProgressBox>
              <ProgressLabel>
                <span>{ehrUploadJob.message || `${ehrUploadJob.current_page} / ${ehrUploadJob.total_pages} EHR forms`}</span>
                <span>{ehrUploadJob.percent.toFixed(1)}%</span>
              </ProgressLabel>
              <ProgressTrack>
                <ProgressFill $pct={ehrUploadJob.percent} $done={ehrDone} $failed={ehrFailed} />
              </ProgressTrack>
              {ehrFailed && <StatusMsg style={{ color: '#ef4444' }}>{ehrUploadJob.error}</StatusMsg>}
            </ProgressBox>
          )}
        </>
      )}

      {activeTab === 'patients' && <PatientListPanel sourceVendor="macpractice" />}
    </Section>
  );
};

// ── EHR system list ───────────────────────────────────────────────────────────

const AVAILABLE_EHR = [
  { value: 'macpractice', label: 'MacPractice' },
  { value: 'janeapp',     label: 'JaneApp' },
] as const;

const COMING_SOON_EHR = [
  'Abelmed', 'Accuro', 'Acuity', 'Acusimple', 'Aesthetic Record',
  'Atlas Chiropractic Software', 'Booker', 'Carepatron', 'Cerbo', 'Charm EHR',
  'ChiroFusion', 'Chirosoft', 'ChiroSpring', 'ChiroSuite', 'ChiroTouch',
  'ChiroTouch Cloud', 'Click4Time', 'ClientTracker', 'Clinic Essentials',
  'ClinicMaster Cloud', 'ClinicMaster Desktop', 'ClinicND', 'Cliniko',
  'ClinicSense', 'CounSol.com', 'Daysmart Appointments', 'DrChrono EHR',
  'Eclipse', 'E-Z Bis', 'EZFacility', 'Fresha', 'Full Slate', 'GlossGenius',
  'Google Calendar/Contacts', 'GOrendezvous', 'Healthquest by Microquest',
  'Heno', 'Innocare', 'Insight', 'IntakeQ', 'Juvonno', 'Massagebook',
  'mBiz', 'Medexa', 'Medisoft', 'Mindbody', 'MRX', 'My Client Schedule',
  'NexySoft', 'Noterro', 'NousTalk', 'OCA Aspire', 'Office Ally',
  'OptimisPT', 'Orchestra One', 'Owl Practice', 'Platinum Chiropractic',
  'Power Diary', 'PMP', 'PPS / PPS Express', 'Practice Better',
  'Practice Fusion', 'Practice Jewel / mindZplay', 'PracticeHub',
  'Practice Perfect', 'Prompt Health', 'PTEverywhere', 'QuickBooks',
  'Schedulicity', 'Schedulista', 'Setmore', 'Sessions Health', 'SimplePractice',
  'SimplyBook.me', 'SmartND', 'Square', 'Tebra', 'TheraLink', 'Ensora Health',
  'TherapyNotes', 'Timely', 'TM2 / TM3', 'Unified Practice',
  'Universal Office / Antibex', 'Vagaro', 'WebPT', 'WellnessLiving',
  'Wix', 'Wolf EMR', 'Write Upp', 'Zenoti', 'Booksy',
];

// ── EHR selector styled components ────────────────────────────────────────────

const EhrSelectorBar = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 14px 18px;
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  margin-bottom: 4px;
`;

const EhrLabel = styled.label`
  font-size: 0.875rem;
  font-weight: 600;
  color: #374151;
  white-space: nowrap;
`;

const EhrSelect = styled.select`
  flex: 1;
  max-width: 320px;
  padding: 8px 12px;
  font-size: 0.875rem;
  border: 1px solid #d1d5db;
  border-radius: 8px;
  background: #fff;
  color: #111827;
  cursor: pointer;
  outline: none;
  &:focus { border-color: #3b82f6; box-shadow: 0 0 0 3px rgba(59,130,246,0.15); }
`;

const ComingSoonCard = styled.div`
  padding: 32px 24px;
  border: 1px dashed #d1d5db;
  border-radius: 10px;
  text-align: center;
  color: #6b7280;
`;

const ComingSoonTitle = styled.div`
  font-size: 1rem;
  font-weight: 600;
  color: #374151;
  margin-bottom: 8px;
`;

// ── PatientIntakeTab — EHR selector wrapper ───────────────────────────────────

const PatientIntakeTab: React.FC = () => {
  const [selectedEhr, setSelectedEhr] = useState<string>('macpractice');
  const activeEhr = AVAILABLE_EHR.find(e => e.value === selectedEhr);
  const comingSoonLabel = COMING_SOON_EHR.find(
    l => l.toLowerCase().replace(/[^a-z0-9]/g, '') === selectedEhr
  );

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
      <EhrSelectorBar>
        <EhrLabel htmlFor="ehr-select">EHR System</EhrLabel>
        <EhrSelect
          id="ehr-select"
          value={selectedEhr}
          onChange={e => setSelectedEhr(e.target.value)}
        >
          <optgroup label="── Available ──">
            {AVAILABLE_EHR.map(e => (
              <option key={e.value} value={e.value}>{e.label}</option>
            ))}
          </optgroup>
          <optgroup label="── Coming Soon ──">
            {COMING_SOON_EHR.map(label => (
              <option key={label} value={label.toLowerCase().replace(/[^a-z0-9]/g, '')}>
                {label}
              </option>
            ))}
          </optgroup>
        </EhrSelect>
      </EhrSelectorBar>

      {selectedEhr === 'macpractice' && <MacPracticeTab />}
      {selectedEhr === 'janeapp'     && <JaneAppTab />}
      {!activeEhr && (
        <ComingSoonCard>
          <ComingSoonTitle>{comingSoonLabel || selectedEhr} — Coming Soon</ComingSoonTitle>
          <div style={{ fontSize: '0.875rem' }}>
            This EHR system is not yet supported. Contact us to request priority integration.
          </div>
        </ComingSoonCard>
      )}
    </div>
  );
};

export default PatientIntakeTab;
